import { Dialog, DialogSurface, DialogTitle, Input, Textarea, RadioGroup, Radio, DialogActions, Button, Field, Subtitle1, Spinner } from "@fluentui/react-components";
import { FC, useState, useRef, useEffect } from "react";
import { TenderAnswer, TenderingFactMapping, Tender } from "../../../models/tendering";
import { CreateTask } from "../../../models/task";
import { createDocument, getDocument } from "../../../services/documentService";
import { createChangeRequestTask } from "../../../services/taskService";
import { updateTenderDocument } from "../../../services/tenderingService";
import { useAppContext } from "../../../AppContext";
import { DismissRegular } from "@fluentui/react-icons";
import { RoleDTO } from "../../../models/role";

interface AnswerChangeProps {
    mapping: TenderingFactMapping;
    tender: Tender;
    open: boolean;
    onClose: (documentCreated: boolean) => void;
    updateAnswer: (mapping: TenderingFactMapping) => void;
    updateTender?: (tender: Tender) => void;
}

type SaveState = 'idle' | 'confirmationCode' | 'sending' | 'sent' | 'error';

const AnswerChange: FC<AnswerChangeProps> = ({ mapping, tender, open, onClose, updateAnswer, updateTender }) => {
    const { user } = useAppContext();
    const [state, setState] = useState<SaveState>('idle');
    const [comment, setComment] = useState(mapping.answer?.comment || '');
    const answerValueRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const [newDocID, setNewDocID] = useState<string | null>(null);
    const [documentCreated, setDocumentCreated] = useState<boolean>(false);
    const [radioValue, setRadioValue] = useState<string>(mapping.answer?.type === 'bool' ? (mapping.answer.value?.toString() ?? '') : '');

    const isEditable = mapping.permission === 'editable' || user?.userType?.system;

    useEffect(() => {
        setComment(mapping.answer?.comment || '');
        setRadioValue(mapping.answer?.type === 'bool' ? (mapping.answer.value?.toString() ?? '') : '');
    }, [mapping]);

    const handleSave = async () => {
        const isBool = mapping.answer?.type === 'bool';
        if (!isBool && !answerValueRef.current) return;

        const newAnswerValue = isBool ? radioValue : answerValueRef.current!.value;
        const newAnswer: TenderAnswer = {
            ...mapping.answer!,
            value: newAnswerValue,
            comment: comment,
            questionID: mapping.questionID,
            tenderID: tender.tenderID
        };

        try {
            // Handle email/phone questions with confirmation code
            // if (isEmail || isPhone) {
            //     const docID = await createDocument({
            //         moduleID: tender.tenderID,
            //         notifyUser: true,
            //         notificationType: isEmail ? 1 : 2,
            //         question: mapping.text,
            //         new: { value: newAnswerValue },
            //         old: mapping.answer
            //     });
                
            //     setNewDocID(docID);
            //     setState('confirmationCode');
            //     return;
            // }

            setState('sending');

            // Non-editable permission: Create change request task
            if (!isEditable && !user.roles.some((r: RoleDTO) => r.roleType === "General")) {
                const docID = await createDocument({
                    moduleID: tender.tenderID,
                    question: mapping.text,
                    new: newAnswer,
                    old: mapping.answer
                });

                const task: CreateTask = {
                    taskName: tender.tenderName,
                    taskDescription: mapping.text,
                    taskData: {
                        notificationTypes: [1],
                        notifySubscribers: true,
                        documentID: docID,
                        tenderModuleID: tender.tenderID,
                        question: mapping.text,
                        answer: newAnswer
                    }
                };

                await createChangeRequestTask(task).then((response) => {
                    setDocumentCreated(true);
                });
                setState('sent');
            } 
            // Editable permission: Update answer directly
            else {
                const updatedTender = { ...tender };
                const answerIndex = updatedTender.tenderAnswers?.findIndex(
                    a => a.questionID === newAnswer.questionID
                );
                
                if (answerIndex !== undefined && answerIndex !== -1 && updatedTender.tenderAnswers) {
                    updatedTender.tenderAnswers[answerIndex] = newAnswer;
                } else if (updatedTender.tenderAnswers) {
                    updatedTender.tenderAnswers.push(newAnswer);
                }

                await updateTenderDocument(tender.tenderID, updatedTender);
                
                if (updateTender) {
                    updateTender(updatedTender);
                }
                
                onClose(false);
            }
        } catch (error) {
            console.error('Error saving answer:', error);
            setState('error');
        }
    };

    const handleClose = () => {
        setState('idle');
        setComment(mapping.answer?.comment || '');
        onClose(documentCreated);
        setDocumentCreated(false);
    };

    const renderCurrentValue = () => {
        if (mapping.answer?.type === 'bool') {
            const val = mapping.answer.value;
            return val === '1' ? 'Ja' : val === '0' ? 'Nej' : 'Inget svar';
        }
        return mapping.answer?.value || '';
    };

    const renderAnswerInput = () => {
        switch (mapping.answer?.type) {
            case 'string':
                return (
                    <Textarea 
                        size="medium" 
                        resize="vertical" 
                        className="w-full" 
                        placeholder="Skriv det nya värdet här..."
                        defaultValue={mapping.answer.value}
                        ref={answerValueRef as any}
                        disabled={state === 'sending'}
                    />
                );
            case 'decimalPriceLinear':
            case 'decimalPrice':
                return (
                    <Input 
                        className="w-full" 
                        type="number" 
                        placeholder="Skriv det nya värdet här..."
                        defaultValue={mapping.answer.value}
                        ref={answerValueRef as any}
                        disabled={state === 'sending'}
                    />
                );
            case 'bool':
                return (
                    <RadioGroup 
                        className="w-full" 
                        value={radioValue}
                        onChange={(_, data) => setRadioValue(data.value)}
                        disabled={state === 'sending'}
                    >
                        <Radio value="" label="Inget svar" />
                        <Radio value="0" label="Nej" />
                        <Radio value="1" label="Ja" />
                    </RadioGroup>
                );
            case 'textarea':
                return (
                    <Textarea 
                        className="w-full" 
                        placeholder="Skriv det nya värdet här..."
                        defaultValue={mapping.answer.value}
                        ref={answerValueRef as any}
                        disabled={state === 'sending'}
                    />
                );
            default:
                return <span>Okänd answertyp: {mapping.answer?.type}</span>;
        }
    };

    const renderDialogContent = () => {
        if (state === 'confirmationCode') {
            return (
                <div className="py-4">
                    <p>En bekräftelsekod har skickats. Vänligen kontrollera din {mapping.question?.questionType === 'email' ? 'e-post' : 'telefon'}.</p>
                    <Field label="Bekräftelsekod" required className="mt-4">
                        <Input />
                    </Field>
                </div>
            );
        }

        if (state === 'sent') {
            return (
                <div className="py-4">
                    <p>Din ändringsbegäran har skickats!</p>
                </div>
            );
        }

        if (state === 'error') {
            return (
                <div className="py-4 text-red-600">
                    <p>Ett fel uppstod när ändringen skulle sparas. Vänligen försök igen.</p>
                </div>
            );
        }

        return (
            <div className="flex flex-col gap-4 py-2">
                <Field label="Nuvarande svar:">
                    <Input
                        className="w-full"
                        value={renderCurrentValue()}
                        readOnly
                        appearance="outline"
                    />
                </Field>

                <div className="flex items-center gap-3">
                    <div className="flex-1" style={{ borderTop: '1px solid #e0e0e0' }} />
                    <span style={{ fontSize: '12px', color: '#999' }}>ersätt med</span>
                    <div className="flex-1" style={{ borderTop: '1px solid #e0e0e0' }} />
                </div>

                <Field
                    label="Nytt svar:"
                    required
                    hint="Skriv in det korrekta värdet som ska ersätta det nuvarande."
                >
                    {renderAnswerInput()}
                </Field>

                <Field label="Kommentar (visas i Upphandlat):">
                    <Input
                        className="w-full"
                        placeholder="T.ex. anledning till ändringen..."
                        value={comment}
                        onChange={(_, data) => setComment(data.value)}
                        disabled={state === 'sending'}
                    />
                </Field>
            </div>
        );
    };

    const renderActions = () => {
        if (state === 'sent') {
            return <Button appearance="primary" onClick={handleClose}>Stäng</Button>;
        }

        if (state === 'error') {
            return (
                <>
                    <Button appearance="secondary" onClick={handleClose}>Stäng</Button>
                    <Button appearance="primary" onClick={handleSave}>Försök igen</Button>
                </>
            );
        }

        if (state === 'confirmationCode') {
            return (
                <>
                    <Button appearance="secondary" onClick={handleClose}>Avbryt</Button>
                    <Button appearance="primary">Verifiera</Button>
                </>
            );
        }

        return (
            <>
                <Button appearance="secondary" onClick={handleClose} disabled={state === 'sending'}>
                    Avbryt
                </Button>
                <Button 
                    appearance="primary" 
                    onClick={handleSave}
                    disabled={state === 'sending'}
                >
                    {state === 'sending' ? <Spinner size="tiny" /> : 'Skicka ändring'}
                </Button>
            </>
        );
    };

    return (
        <Dialog modalType="alert" open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
            <DialogSurface>
                <DialogTitle
                  style={{ flexShrink: 0 }}
                  action={
                  <Button
                    shape="circular"
                    appearance="subtle"
                    icon={<DismissRegular />}
                    onClick={handleClose}
                    style={{ position: 'absolute', top: '20px', right: '20px' }}
                  />
                }
                >
                    <div className="flex flex-col gap-0.5">
                        {state !== 'sent' && state !== 'confirmationCode' && (
                            <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888' }}>
                                Begär ändring av
                            </span>
                        )}
                        <Subtitle1 style={{ flexShrink: 0 }}>
                            {state === 'sent' ? 'Ändring skickad' : state === 'confirmationCode' ? 'Bekräfta ändring' : mapping.text}
                        </Subtitle1>
                    </div>
                </DialogTitle>
                {renderDialogContent()}
                <DialogActions className="py-2" style={{ flexShrink: 0 }}>
                    {renderActions()}
                </DialogActions>
            </DialogSurface>
        </Dialog>
    );
};

export default AnswerChange;
